import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth, useUserMap } from "@/context/AuthContext";
import { fmtDate, fmtDateLong, todayStr } from "@/lib/format";
import { Badge } from "@/components/Badge";
import { CONTENT_STATUS, CONTENT_STATUS_ORDER, PLATFORMS, CONTENT_TYPES, PRIORITY, SEMANTIC_DOT, label } from "@/lib/meta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { FestivalDetail } from "@/components/FestivalDetail";
import { toast } from "sonner";

function mondayOf(d) {
  const dt = new Date(d + "T00:00:00");
  const day = (dt.getDay() + 6) % 7; // Mon=0
  dt.setDate(dt.getDate() - day);
  return dt;
}
function iso(dt) {
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}

export const emptyContent = (date, client_id) => ({
  client_id: client_id || "", date: date || todayStr(), planned_time: "", platform: "instagram", content_type: "static",
  title: "", caption: "", brief: "", assigned_to: "", reviewer_id: "", status: "planned", priority: "medium", notes: "", festival_id: null,
});

export function ContentDialog({ open, onOpenChange, initial, defaultClient, onSaved }) {
  const { users } = useAuth();
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });
  const [f, setF] = useState(initial || emptyContent(null, defaultClient));
  const [saving, setSaving] = useState(false);
  React.useEffect(() => { setF(initial || emptyContent(null, defaultClient)); }, [initial, open, defaultClient]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.client_id) return toast.error("Select a client");
    if (!f.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const payload = { ...f, assigned_to: f.assigned_to || null, reviewer_id: f.reviewer_id || null };
      if (initial?.id) await api.put(`/content/${initial.id}`, payload);
      else await api.post("/content", payload);
      toast.success(initial?.id ? "Content updated" : "Content added");
      onOpenChange(false); onSaved && onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const del = async () => { if (!initial?.id) return; await api.delete(`/content/${initial.id}`); toast.success("Deleted"); onOpenChange(false); onSaved && onSaved(); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{initial?.id ? "Edit Content" : "Add Content"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-3 py-2 sm:grid-cols-2">
          <div className="sm:col-span-2"><Label className="text-xs text-zinc-500">Title *</Label><Input value={f.title} onChange={(e) => set("title", e.target.value)} className="mt-1" data-testid="content-title-input" /></div>
          <div>
            <Label className="text-xs text-zinc-500">Client *</Label>
            <Select value={f.client_id} onValueChange={(v) => set("client_id", v)} disabled={!!defaultClient}>
              <SelectTrigger className="mt-1" data-testid="content-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs text-zinc-500">Date</Label><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className="mt-1" /></div>
            <div><Label className="text-xs text-zinc-500">Time</Label><Input type="time" value={f.planned_time || ""} onChange={(e) => set("planned_time", e.target.value)} className="mt-1" /></div>
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Platform</Label>
            <Select value={f.platform} onValueChange={(v) => set("platform", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Content Type</Label>
            <Select value={f.content_type} onValueChange={(v) => set("content_type", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{CONTENT_TYPES.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Status</Label>
            <Select value={f.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className="mt-1" data-testid="content-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CONTENT_STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{CONTENT_STATUS[s].label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Assigned To</Label>
            <Select value={f.assigned_to || "none"} onValueChange={(v) => set("assigned_to", v === "none" ? "" : v)}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Unassigned</SelectItem>{users.filter((u) => u.active).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Priority</Label>
            <Select value={f.priority} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(PRIORITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><Label className="text-xs text-zinc-500">Caption</Label><Textarea value={f.caption} onChange={(e) => set("caption", e.target.value)} className="mt-1" rows={2} /></div>
          <div className="sm:col-span-2"><Label className="text-xs text-zinc-500">Brief / Notes</Label><Input value={f.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1" /></div>
        </div>
        <DialogFooter className="flex items-center justify-between sm:justify-between">
          {initial?.id ? <Button variant="ghost" onClick={del} className="text-red-600"><Trash2 className="mr-2 h-4 w-4" />Delete</Button> : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="content-save-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function ContentCalendar() {
  const qc = useQueryClient();
  const [anchor, setAnchor] = useState(todayStr());
  const [clientId, setClientId] = useState("all");
  const [statusF, setStatusF] = useState("all");
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [defDate, setDefDate] = useState(null);
  const [festDetail, setFestDetail] = useState(null);
  const [planFest, setPlanFest] = useState(null);

  const monday = useMemo(() => mondayOf(anchor), [anchor]);
  const weekStart = iso(monday);
  const days = useMemo(() => DAYS.map((_, i) => { const d = new Date(monday); d.setDate(d.getDate() + i); return iso(d); }), [monday]);

  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["content", weekStart, clientId],
    queryFn: async () => (await api.get("/content", { params: { week: weekStart, client_id: clientId === "all" ? undefined : clientId } })).data,
  });
  const { data: festivals = [] } = useQuery({ queryKey: ["festivals", "all"], queryFn: async () => (await api.get("/festivals?all_year=true")).data });
  const festByDate = useMemo(() => { const m = {}; festivals.forEach((f) => { (m[f.date] = m[f.date] || []).push(f); }); return m; }, [festivals]);

  const filtered = items.filter((i) => statusF === "all" || i.status === statusF);
  const byDate = {}; filtered.forEach((i) => { (byDate[i.date] = byDate[i.date] || []).push(i); });
  const cmap = {}; clients.forEach((c) => (cmap[c.id] = c.name));

  const shiftWeek = (dir) => { const d = new Date(monday); d.setDate(d.getDate() + dir * 7); setAnchor(iso(d)); };
  const openNew = (date) => { setEditing(null); setPlanFest(null); setDefDate(date); setDialog(true); };
  const openEdit = (it) => { setEditing(it); setPlanFest(null); setDefDate(null); setDialog(true); };
  const planForFestival = (f) => { setFestDetail(null); setPlanFest(f); setEditing(null); setDefDate(null); setDialog(true); };

  const dialogInitial = planFest
    ? { ...emptyContent(planFest.date, clientId === "all" ? "" : clientId), festival_id: planFest.id, title: planFest.name, content_type: "festival" }
    : (editing || (defDate ? emptyContent(defDate, clientId === "all" ? "" : clientId) : (clientId !== "all" ? emptyContent(null, clientId) : null)));

  const sat = new Date(monday); sat.setDate(sat.getDate() + 5);

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Content Calendar</h1>
          <p className="mt-1 text-sm text-zinc-500">Weekly planning · {fmtDate(weekStart)} → {fmtDateLong(iso(sat))}</p>
        </div>
        <Button onClick={() => openNew(null)} data-testid="add-content-button"><Plus className="mr-2 h-4 w-4" />Add Content</Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 rounded-md border border-zinc-200 bg-white">
          <button onClick={() => shiftWeek(-1)} className="p-2 hover:bg-zinc-50" data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></button>
          <span className="min-w-[150px] text-center text-sm font-medium">{fmtDate(weekStart)} – {fmtDate(iso(sat))}</span>
          <button onClick={() => shiftWeek(1)} className="p-2 hover:bg-zinc-50" data-testid="cal-next"><ChevronRight className="h-4 w-4" /></button>
          <button onClick={() => setAnchor(todayStr())} className="border-l border-zinc-200 px-3 py-2 text-xs font-medium hover:bg-zinc-50">Today</button>
        </div>
        <Select value={clientId} onValueChange={setClientId}>
          <SelectTrigger className="w-48" data-testid="cal-client-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Clients</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger className="w-44" data-testid="cal-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem>{CONTENT_STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{CONTENT_STATUS[s].label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {days.map((date, i) => (
            <Card key={date} className={`group flex min-h-[220px] flex-col rounded-lg border-zinc-200 shadow-sm ${date === todayStr() ? "ring-2 ring-blue-200" : ""}`}>
              <div className="flex items-center justify-between border-b border-zinc-100 px-3 py-2">
                <div><div className="text-xs font-semibold">{DAYS[i]}</div><div className="text-[11px] text-zinc-400">{fmtDate(date)}</div></div>
                <button onClick={() => openNew(date)} data-testid={`cal-add-${date}`} className="opacity-0 transition-opacity group-hover:opacity-100"><Plus className="h-4 w-4 text-zinc-400 hover:text-zinc-900" /></button>
              </div>
              <div className="flex-1 space-y-1.5 p-2">
                {(festByDate[date] || []).map((f) => (
                  <button key={f.id} onClick={() => setFestDetail(f)} data-testid={`cal-festival-${f.id}`} className="flex w-full items-center gap-1.5 rounded-md border border-rose-100 bg-rose-50 px-2 py-1 text-left transition-colors hover:bg-rose-100">
                    <span className="text-sm leading-none">{f.emoji}</span>
                    <span className="truncate text-[11px] font-semibold text-rose-700">{f.name}</span>
                    {f.total_clients > 0 && (
                      <span className={`ml-auto shrink-0 rounded-full px-1.5 text-[9px] font-bold ${f.planned_count >= f.total_clients ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{f.planned_count}/{f.total_clients}</span>
                    )}
                  </button>
                ))}
                {(byDate[date] || []).map((it) => (
                  <button key={it.id} onClick={() => openEdit(it)} data-testid={`cal-item-${it.id}`} className="w-full rounded-md border border-zinc-100 bg-white p-2 text-left hover:bg-zinc-50">
                    <div className="flex items-center gap-1.5">
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${SEMANTIC_DOT[CONTENT_STATUS[it.status]?.color]}`} />
                      <span className="truncate text-xs font-medium text-zinc-800">{it.title}</span>
                    </div>
                    <div className="mt-0.5 truncate text-[10px] text-zinc-400">{cmap[it.client_id]}</div>
                  </button>
                ))}
                {(byDate[date] || []).length === 0 && (festByDate[date] || []).length === 0 && <div className="pt-6 text-center text-[11px] text-zinc-300">—</div>}
              </div>
            </Card>
          ))}
        </div>
      )}

      <ContentDialog
        open={dialog}
        onOpenChange={setDialog}
        initial={dialogInitial}
        onSaved={() => { qc.invalidateQueries({ queryKey: ["content"] }); qc.invalidateQueries({ queryKey: ["festivals"] }); }}
      />
      <FestivalDetail fest={festDetail} onClose={() => setFestDetail(null)} onPlan={planForFestival} />
    </div>
  );
}
